import { Drawer } from "vaul";
import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";

export interface BottomSheetProps {
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
  snapPoints?: (number | string)[];
  activeSnapPoint?: number | string | null;
  setActiveSnapPoint?: (snapPoint: number | string | null) => void;
  shouldScaleBackground?: boolean;
  disablePreventScroll?: boolean;
  className?: string;
}

/**
 * BottomSheet SOTA ultime basé sur Vaul (Emil Kowalski / Radix Ecosystem).
 * Offre le véritable glissement tactile natif iOS/Android (swipe to dismiss),
 * les snapPoints, la vélocité tactile à 0ms de latence et l'effet d'échelle sur l'arrière-plan.
 * Garanti zéro débordement à droite (box-border max-w-full inset-x-0).
 */
export function BottomSheet({
  open,
  onClose,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissible = true,
  snapPoints,
  activeSnapPoint,
  setActiveSnapPoint,
  shouldScaleBackground = false,
  disablePreventScroll,
  className,
}: BottomSheetProps) {
  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange?.(isOpen);
    if (!isOpen) onClose?.();
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      dismissible={dismissible}
      snapPoints={snapPoints}
      activeSnapPoint={activeSnapPoint}
      setActiveSnapPoint={setActiveSnapPoint}
      shouldScaleBackground={shouldScaleBackground}
      disablePreventScroll={disablePreventScroll}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-surface-overlay/60 backdrop-blur-md transition-opacity duration-fast" />
        <Drawer.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fixed left-0 right-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-2xl border-t border-border bg-surface-raised shadow-overlay box-border w-full max-w-full overflow-hidden pb-safe outline-none",
            "sm:max-w-lg sm:mx-auto sm:rounded-2xl sm:bottom-4 sm:border-x",
            className,
          )}
        >
          {/* Poignée de glisser tactile SOTA (Vaul Handle) */}
          <Drawer.Handle className="my-3 h-1.5 w-12 shrink-0 rounded-full bg-border-strong mx-auto cursor-grab active:cursor-grabbing touch-none select-none" />

          {/* En-tête de la feuille */}
          {(title || description) && (
            <div className="flex items-start justify-between gap-4 border-b border-border px-5 pb-3 pt-1">
              <div className="flex flex-col gap-0.5">
                {title && (
                  <Drawer.Title className="text-base font-semibold tracking-tight text-fg">
                    {title}
                  </Drawer.Title>
                )}
                {description && (
                  <Drawer.Description className="text-xs text-fg-muted">
                    {description}
                  </Drawer.Description>
                )}
              </div>
              {dismissible && (
                <Drawer.Close asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Fermer">
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      className="size-4"
                    >
                      <path d="m5 5 10 10M15 5 5 15" />
                    </svg>
                  </Button>
                </Drawer.Close>
              )}
            </div>
          )}

          {/* Corps de contenu avec défilement fluide */}
          {children && (
            <div className="flex-1 overflow-y-auto p-5 text-sm text-fg [webkit-overflow-scrolling:touch]">
              {children}
            </div>
          )}

          {/* Pied de page optionnel */}
          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-border p-4 bg-surface-sunken/40">
              {footer}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export function BottomSheetHeader({ className, children, ...props }: ComponentPropsWithRef<"div">) {
  return (
    <div className={cn("flex flex-col gap-1 border-b border-border pb-3", className)} {...props}>
      {children}
    </div>
  );
}

export function BottomSheetTitle({ className, children, ...props }: ComponentPropsWithRef<"h2">) {
  return (
    <h2 className={cn("text-base font-semibold text-fg", className)} {...props}>
      {children}
    </h2>
  );
}

export function BottomSheetBody({ className, children, ...props }: ComponentPropsWithRef<"div">) {
  return (
    <div className={cn("flex-1 overflow-y-auto py-3", className)} {...props}>
      {children}
    </div>
  );
}

export function BottomSheetFooter({ className, children, ...props }: ComponentPropsWithRef<"div">) {
  return (
    <div className={cn("flex items-center justify-end gap-2 pt-3 border-t border-border", className)} {...props}>
      {children}
    </div>
  );
}

// Primitives Vaul ré-exportées pour compositions avancées
export const BottomSheetRoot = Drawer.Root;
export const BottomSheetTrigger = Drawer.Trigger;
export const BottomSheetPortal = Drawer.Portal;
export const BottomSheetOverlay = Drawer.Overlay;
export const BottomSheetContent = Drawer.Content;
export const BottomSheetDescription = Drawer.Description;
export const BottomSheetClose = Drawer.Close;
export const BottomSheetHandle = Drawer.Handle;
